// Artifacts beside the lead's thread (docs/prime-panel.md): the pages Prime on
// Claude Code has shown (MCP `show`, recorded on its run as status.artifacts),
// newest first, each in the real page editor, so the owner edits what Prime
// writes and each sees the other's changes as they sync. On a wide screen they
// dock to the right of the thread (DockAside, like Prime beside a page); on a
// phone they open in a sheet from the thread's top bar. An artifact is an
// ordinary Page: saved, searchable, in Notes, undoable; closing a tab takes it
// from beside the thread and nothing more.
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronRight, ExternalLink, FileText, X } from 'lucide-react'
import { mutate } from '@/runtime'
import { useAllPages, useLook } from '@/db/hooks'
import type { AgentRunResource } from '@/kinds/agentrun'
import type { PageResource } from '@/kinds/page/apply'
import type { EditorApi } from '@/kinds/page/editor'
import { PageGlyph } from '@/kinds/page/components'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { isLightTheme } from '@/lib/colour'
import { PANEL_QUERY } from '@/lib/panel'
import { useArtifactPanel } from '@/state/panel'
import { useUi } from '@/state/ui'
import { ConflictBanner } from '@/views/page/ConflictBanner'
import { DockAside, DockHandle } from './Dock'
import { IconButton, Sheet, cx } from './ui'

// BlockNote is ~1 MB: only a thread with an artifact open pays for it
const PageEditor = lazy(() => import('@/kinds/page/editor').then((m) => ({ default: m.PageEditor })))

/**
 * Beside Prime's thread a version arriving soon after a save here is a
 * conflict for only a short while, since the server refuses Prime's rewrite
 * unless Prime read the newest version it holds, and checks that again as the
 * write commits (internal/assistant/artifacts.go). An edit that has not reached
 * the server yet (made offline, say) is caught by the editor's outbox check
 * however long ago it was typed, so this window is not what keeps it.
 */
const ARTIFACT_CONFLICT_MS = 15_000

/** The live pages shown beside a lead run, newest first. */
export const useArtifacts = (run: AgentRunResource | undefined): PageResource[] => {
  const all = useAllPages()
  const shown = run?.status.artifacts
  return useMemo(() => {
    const byId = new Map((all ?? []).map((p) => [p.id, p]))
    return (shown ?? []).flatMap((a) => {
      const p = byId.get(a.id)
      return p && !p.deleted_at ? [p] : []
    })
  }, [all, shown])
}

/** Whether the artifacts are docked beside the thread on this screen now. */
export const useArtifactsDocked = (run: AgentRunResource | undefined): boolean => {
  const wide = useMediaQuery(PANEL_QUERY)
  const open = useArtifactPanel((s) => s.open)
  const any = useArtifacts(run).length > 0
  return wide && open && any
}

/** The newest artifact's id; something new shown is worth a look (open the dock, light the button). */
const useNewest = (run: AgentRunResource) => run.status.artifacts?.[0]

const SEEN_KEY = (runId: string) => `artifact-seen:${runId}`

const readSeen = (runId: string): string => {
  try {
    return localStorage.getItem(SEEN_KEY(runId)) ?? ''
  } catch {
    return ''
  }
}

const writeSeen = (runId: string, at: string) => {
  try {
    localStorage.setItem(SEEN_KEY(runId), at)
  } catch {
    /* private mode: the dot comes back next time */
  }
}

/** One artifact in the editor, with the conflict choice above it when there is one. */
export const ArtifactEditor = ({ page }: { page: PageResource }) => {
  const look = useLook()
  const light = isLightTheme(look.theme, look.palette)
  const [api, setApi] = useState<EditorApi | null>(null)
  const [conflict, setConflict] = useState<unknown[] | null>(null)
  return (
    <div className="artifact-doc min-w-0" data-testid="artifact-editor" data-page={page.id}>
      <h2 className="t-title pl-9 pr-2 md:pl-11 pointer-coarse:px-4 mb-2 break-words">{page.name}</h2>
      {conflict && api && (
        <ConflictBanner
          theirs={conflict}
          assistant={false}
          onKeepMine={() => api.keepMine()}
          onTakeTheirs={() => api.takeTheirs(conflict)}
          onMerge={async () => {}}
        />
      )}
      <Suspense fallback={<div className="min-h-[30vh] text-sm text-faint px-2 py-4">Loading editor…</div>}>
        <PageEditor
          key={page.id}
          page={page}
          light={light}
          onReady={setApi}
          onConflict={setConflict}
          conflictWindowMs={ARTIFACT_CONFLICT_MS}
        />
      </Suspense>
    </div>
  )
}

/** The tabs (newest first) and the chosen artifact's editor. */
const ArtifactBody = ({
  run,
  pages,
  onHide,
}: {
  run: AgentRunResource
  pages: PageResource[]
  /** the dock's chevron; the sheet has its own close */
  onHide?: () => void
}) => {
  const flash = useUi((s) => s.showFlash)
  const newest = pages[0]?.id
  // a pick holds until something newer is shown, which then comes first
  const [chosen, setChosen] = useState<{ id: string; newest?: string } | null>(null)
  const currentId =
    chosen && chosen.newest === newest && pages.some((p) => p.id === chosen.id) ? chosen.id : newest
  const page = pages.find((p) => p.id === currentId)
  const close = async (p: PageResource) => {
    try {
      await mutate.action(run.id, 'close_artifact', { page_id: p.id })
      flash(`Closed “${p.name}”. It is still in Notes.`, 3000)
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not close it', 3000)
    }
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-1 border-b border-line px-2 py-1.5">
        <div
          className="flex min-w-0 flex-1 gap-1 overflow-x-auto"
          role="tablist"
          aria-label="Artefacts"
          data-testid="artifact-tabs"
        >
          {pages.map((p) => (
            <span
              key={p.id}
              className={cx(
                'flex shrink-0 items-center rounded-full max-w-[14rem]',
                p.id === currentId ? 'bg-accent-soft text-fg' : 'text-muted hover:text-fg',
              )}
            >
              <button
                type="button"
                role="tab"
                aria-selected={p.id === currentId}
                onClick={() => setChosen({ id: p.id, newest })}
                className="flex min-h-8 min-w-0 items-center gap-1.5 pl-3 pr-1 text-sm"
                data-testid="artifact-tab"
              >
                <PageGlyph page={p} size={14} />
                <span className="truncate">{p.name}</span>
              </button>
              <button
                type="button"
                onClick={() => void close(p)}
                className="grid size-7 shrink-0 place-items-center rounded-full text-faint hover:text-fg"
                aria-label={`Close ${p.name}`}
                title="Take it from beside the thread (the page stays in Notes)"
                data-testid="artifact-close"
              >
                <X size={13} aria-hidden />
              </button>
            </span>
          ))}
        </div>
        {page && (
          <Link
            to="/pages/$id"
            params={{ id: page.id }}
            className="grid size-8 shrink-0 place-items-center rounded-full text-muted hover:text-fg"
            aria-label="Open as a page"
            title="Open as a page"
            data-testid="artifact-open"
          >
            <ExternalLink size={15} aria-hidden />
          </Link>
        )}
        {onHide && (
          <button
            type="button"
            onClick={onHide}
            className="grid size-8 shrink-0 place-items-center rounded-full text-muted hover:text-fg"
            aria-label="Move the artefacts out of the way"
            title="Hide"
            data-testid="artifact-panel-hide"
          >
            <ChevronRight size={16} aria-hidden />
          </button>
        )}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto py-3 pr-2">
        {page && <ArtifactEditor key={page.id} page={page} />}
      </div>
    </div>
  )
}

/** The artifacts docked to the right of the lead's thread, on a wide screen. */
export const ArtifactDock = ({ run }: { run: AgentRunResource }) => {
  const wide = useMediaQuery(PANEL_QUERY)
  const pages = useArtifacts(run)
  const open = useArtifactPanel((s) => s.open)
  const width = useArtifactPanel((s) => s.width)
  const setWidth = useArtifactPanel((s) => s.setWidth)
  const toggle = useArtifactPanel((s) => s.toggle)
  const newest = useNewest(run)

  // Prime showing something new is the point of its turn: bring the dock back,
  // the first artifact on a thread that had none included ('' is "none yet")
  const shown = useRef(newest?.at ?? '')
  useEffect(() => {
    if (newest?.at && newest.at > shown.current) useArtifactPanel.getState().setOpen(true)
    shown.current = newest?.at ?? ''
  }, [newest?.at])

  if (!wide || !pages.length) return null
  if (!open)
    return (
      <DockHandle
        onClick={toggle}
        label="Show the artefacts beside the thread"
        title="Show the artefacts"
        testId="artifact-panel-handle"
      >
        <FileText size={14} aria-hidden />
      </DockHandle>
    )
  return (
    <DockAside width={width} onWidth={setWidth} label="Artefacts" testId="artifact-panel">
      <ArtifactBody run={run} pages={pages} onHide={toggle} />
    </DockAside>
  )
}

/**
 * The way to the artifacts where they do not dock (a phone or a narrow
 * window): a button for the thread's top bar, lit when something was shown
 * since it was last opened, and the sheet it opens. On a wide screen with the
 * dock hidden the dock's edge handle is the way back, so this stays away.
 */
export const ArtifactButton = ({ run }: { run: AgentRunResource }) => {
  const wide = useMediaQuery(PANEL_QUERY)
  const pages = useArtifacts(run)
  const newest = useNewest(run)
  const [open, setOpen] = useState(false)
  const [seen, setSeen] = useState(() => readSeen(run.id))
  if (wide || !pages.length) return null
  const fresh = !!newest?.at && newest.at > seen
  const show = () => {
    if (newest?.at) {
      writeSeen(run.id, newest.at)
      setSeen(newest.at)
    }
    setOpen(true)
  }
  return (
    <>
      <IconButton label="Artefacts" onClick={show} data-testid="artifact-button">
        <span className="relative">
          <FileText size={20} aria-hidden />
          {fresh && (
            <span
              className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-accent"
              aria-label="Something new"
              data-testid="artifact-new"
            />
          )}
        </span>
      </IconButton>
      <Sheet open={open} onClose={() => setOpen(false)} title="Artefacts">
        <div className="-mx-4 flex min-h-[60dvh] flex-col" data-testid="artifact-sheet">
          <ArtifactBody run={run} pages={pages} />
        </div>
      </Sheet>
    </>
  )
}
